import { createSelector } from "@reduxjs/toolkit";
import type {
  PostMcbimItemsApiArg,
  PostMcbimItemsApiResponse,
} from "../../openapi";
import { openapi } from "../../openapi";
import type { IRootState } from "../app/store";
import { selectShowRoomSectionPerimeterItems } from "../DesignTab/designSlice";
import { selectProjectNo } from "../globalSelectors";
import { selectHttpActiveFullMcbimSpace } from "../McBim/mcbimSelectors";

const ROOM_SECTION_PERIMETER_PADDING_M = 0.5;
const ROOM_SECTION_PERIMETER_INSTANCE_CLASSES = [
  "IfcWall",
  "IfcDoor",
  "IfcWindow",
  "IfcCurtainWall",
];

const EMPTY_PERIMETER_ITEMS: PostMcbimItemsApiResponse = [];

type Bounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

const getPointBounds = (points: number[][] | null | undefined): Bounds | null => {
  if (!points?.length) {
    return null;
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    const [x, y] = point;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }

    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  if (![minX, maxX, minY, maxY].every(Number.isFinite)) {
    return null;
  }

  return { minX, maxX, minY, maxY };
};

const expandBounds = (bounds: Bounds, paddingM: number): Bounds => ({
  minX: bounds.minX - paddingM,
  maxX: bounds.maxX + paddingM,
  minY: bounds.minY - paddingM,
  maxY: bounds.maxY + paddingM,
});

export const selectRoomSectionPerimeterItemsArgs = createSelector(
  [selectProjectNo, selectHttpActiveFullMcbimSpace, selectShowRoomSectionPerimeterItems],
  (projectNo, mcbimSpace, showPerimeterItems): PostMcbimItemsApiArg | null => {
    if (!showPerimeterItems) {
      return null;
    }

    const buildingStoryName = mcbimSpace?.buildingStory?.name;
    if (!projectNo || !buildingStoryName) {
      return null;
    }

    const roomPoints =
      mcbimSpace.geometry?.polygon2d ??
      mcbimSpace.geometry?.bbox2d ??
      mcbimSpace.polygon ??
      mcbimSpace.bbox;
    const bounds = getPointBounds(roomPoints);
    if (!bounds) {
      return null;
    }

    return {
      body: {
        projectNo,
        buildingStoryName,
        instanceClasses: ROOM_SECTION_PERIMETER_INSTANCE_CLASSES,
        centerPointBounds: expandBounds(bounds, ROOM_SECTION_PERIMETER_PADDING_M),
        includeGeometry: true,
      },
    };
  }
);

export const selectRoomSectionPerimeterItems = createSelector(
  [selectRoomSectionPerimeterItemsArgs, (state: IRootState) => state],
  (args, state): PostMcbimItemsApiResponse => {
    if (!args) {
      return EMPTY_PERIMETER_ITEMS;
    }

    return openapi.endpoints.postMcbimItems.select(args)(state).data ?? EMPTY_PERIMETER_ITEMS;
  }
);
